import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Upload, ImagePlus, Loader2 } from "lucide-react";
import { adminClient, API } from "./api";

export default function ImageUploader({ value, onChange, testId = "image-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("Only images allowed");
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminClient.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      onChange(fullUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId} className="space-y-2">
      {value ? (
        <div className="relative rounded-md overflow-hidden border-2 border-zinc-200 h-40">
          <img src={value} alt="product" className="w-full h-full object-cover" />
          <button
            type="button"
            data-testid={`${testId}-change-btn`}
            onClick={() => fileRef.current?.click()}
            className="absolute inset-0 bg-black/0 hover:bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
          >
            <span className="bg-white rounded-md px-3 py-1.5 text-xs font-bold flex items-center gap-1">
              <ImagePlus className="w-3.5 h-3.5" /> Change
            </span>
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testId}-upload-btn`}
          onClick={() => fileRef.current?.click()}
          className="w-full border-2 border-dashed border-zinc-300 rounded-md p-6 flex flex-col items-center justify-center gap-1 hover:border-[#0C2E2B] transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
              <span className="text-xs text-zinc-500 font-bold">Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-zinc-400" />
              <span className="text-xs text-zinc-600 font-bold">Click to upload image</span>
              <span className="text-[10px] text-zinc-400">JPEG / PNG / WEBP · up to 8MB</span>
            </>
          )}
        </button>
      )}
      <input
        ref={fileRef}
        data-testid={`${testId}-file-input`}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}
